import { FAVORITE_DELIVERY_TYPE } from 'common/constants';
import React from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  List,
  ListItem,
  Portal,
  RegularButton,
  SelectionGroup,
  Typography,
} from 'fronton-react';
import { PencilSimpleIcon, TrashIcon } from '@fronton/icons-react';
import { FavoriteDeliveryType } from 'types/checkout-info';
import { DeletableFavoriteAddressType } from 'types/favorite-address';
import { UseCityPickerStateReturnType } from '../../hooks';
import { useAddressMenu } from './hooks';
import { SelectionGroupAddressItem } from './selection-group-address-item';
import { drawerDescription, selectionGroup, addressActionsMenu, addressActionsList, red } from './styles';
import { getAddressSheetItemDisplay } from './utils';
import { HeaderModelData } from 'types/model';
import { DeliveryMethodsMapModalRemote } from './delivery-methods-map-modal-remote';

interface AddressMenuProps extends UseCityPickerStateReturnType {
  visible: boolean;
  model: HeaderModelData;
}

export const AddressMenu = ({
  show,
  onMenuClose,
  onRegionsMenuOpen,
  visible,
  model,
  onMenuOpen,
}: AddressMenuProps) => {
  const {
    t,
    favoriteAddress,
    deliveryType,
    setDeliveryType,
    selectionGroupList,
    deletedAddressTypesSet,
    regionName,
    checkoutInfo,
    handleConfirm,
    handleItemIconClick,
    isMenuOpen,
    menuCoords,
    menuRef,
    handleMenuSelect,
    isVisible,
    regionId,
    selectedMapDeliveryType,
    onRemoteClose,
    onRemoteApply,
    onNewAddressClick,
    selectedFilters,
  } = useAddressMenu({ show, model, onMenuClose, onRegionsMenuOpen, onMenuOpen });

  if (!visible) return null;

  return (
    <>
      <Drawer size="s" direction="right" show={show} onClose={onMenuClose}>
        <DrawerHeader
          title={t('header.main.favorite-address.address-menu.title')}
          onClickBack={onMenuClose}
        />
        <DrawerContent>
          <Typography className={drawerDescription} variant="s" size="body_long">
            {t('header.main.favorite-address.address-menu.description')}
          </Typography>
          <SelectionGroup
            className={selectionGroup}
            variant="composite"
            extraCheck={{
              type: 'radio',
              size: 'm',
            }}
            onChange={(_, value) => setDeliveryType(value as FavoriteDeliveryType)}
            active={deliveryType}
          >
            {selectionGroupList.map(({ id, name, description, priceText, icon }) => {
              const isDeleted = deletedAddressTypesSet.has(id as DeletableFavoriteAddressType);

              return (
                <SelectionGroupAddressItem
                  key={id}
                  value={id}
                  name={name}
                  description={description}
                  priceText={
                    id === FAVORITE_DELIVERY_TYPE.HOME_DELIVERY
                      ? (favoriteAddress.data.minPriceText ?? undefined)
                      : priceText
                  }
                  icon={icon}
                  showIcon={!isDeleted}
                  addressDisplay={
                    isDeleted
                      ? null
                      : getAddressSheetItemDisplay(
                          id,
                          t,
                          checkoutInfo,
                          regionName,
                          favoriteAddress.data.address ?? undefined,
                        )
                  }
                  onCardClick={handleItemIconClick}
                />
              );
            })}
          </SelectionGroup>
        </DrawerContent>
        <DrawerFooter sticky>
          <RegularButton variant="secondary" onClick={onNewAddressClick}>
            {t('header.main.favorite-address.address-menu.new-address')}
          </RegularButton>
          <RegularButton variant="primary" onClick={handleConfirm}>
            {t('header.main.favorite-address.address-menu.confirm')}
          </RegularButton>
        </DrawerFooter>

        {isMenuOpen && menuCoords && (
          <Portal>
            <div
              ref={menuRef}
              className={addressActionsMenu}
              style={{ top: menuCoords.top, left: menuCoords.left }}
            >
              <List
                className={addressActionsList}
                onClick={(value) => handleMenuSelect(value as 'edit' | 'delete')}
              >
                <ListItem
                  iconLeft={<PencilSimpleIcon />}
                  text={t('header.main.favorite-address.address-menu.actions.edit')}
                  value="edit"
                />
                <ListItem
                  className={red}
                  iconLeft={<TrashIcon type="duotone" color="attention-primary" />}
                  text={t('header.main.favorite-address.address-menu.actions.delete')}
                  value="delete"
                />
              </List>
            </div>
          </Portal>
        )}
      </Drawer>
      {isVisible && (
        <DeliveryMethodsMapModalRemote
          visible={isVisible}
          regionId={regionId}
          selectedDeliveryType={selectedMapDeliveryType}
          selectedFilters={selectedFilters}
          onRemoteClose={onRemoteClose}
          onRemoteApply={onRemoteApply}
        />
      )}
    </>
  );
};
